// The countries the course downloader can target, and the area within each
// that a download can be confined to — county, state, province, whatever that
// country calls it (`regionLabel`). The chosen area goes to the server as
// `region`, which confines list generation to it and becomes each course's
// `location` for discovery.
//
// `regions` is either a flat list of names, or a list of groups
// ({ group, options }). A group is also selectable as a whole ("All of
// Scotland"), unless it is only a `heading` ("Golf coasts" is not a place)
// or has one option of the same name (Hokkaido).
// `qualify` appends the group to an option's value — "Kent, England" — where
// the group is a place a search engine should hear (the UK's nations), and
// not where it is only a heading ("Golf coasts").

const UK = [
  { group: 'England', qualify: true, options: [
    'Bedfordshire', 'Berkshire', 'Bristol', 'Buckinghamshire', 'Cambridgeshire',
    'Cheshire', 'Cornwall', 'County Durham', 'Cumbria', 'Derbyshire', 'Devon',
    'Dorset', 'East Riding of Yorkshire', 'East Sussex', 'Essex', 'Gloucestershire',
    'Greater London', 'Greater Manchester', 'Hampshire', 'Herefordshire',
    'Hertfordshire', 'Isle of Wight', 'Kent', 'Lancashire', 'Leicestershire',
    'Lincolnshire', 'Merseyside', 'Norfolk', 'North Yorkshire', 'Northamptonshire',
    'Northumberland', 'Nottinghamshire', 'Oxfordshire', 'Rutland', 'Shropshire',
    'Somerset', 'South Yorkshire', 'Staffordshire', 'Suffolk', 'Surrey',
    'Tyne and Wear', 'Warwickshire', 'West Midlands', 'West Sussex',
    'West Yorkshire', 'Wiltshire', 'Worcestershire',
  ] },
  { group: 'Scotland', qualify: true, options: [
    'Aberdeen', 'Aberdeenshire', 'Angus', 'Argyll and Bute', 'Ayrshire',
    'Clackmannanshire', 'Dumfries and Galloway', 'Dunbartonshire', 'Dundee',
    'East Lothian', 'Edinburgh', 'Falkirk', 'Fife', 'Glasgow', 'Highlands',
    'Inverclyde', 'Lanarkshire', 'Midlothian', 'Moray', 'Orkney',
    'Perth and Kinross', 'Renfrewshire', 'Scottish Borders', 'Shetland',
    'Stirling', 'West Lothian', 'Western Isles',
  ] },
  { group: 'Wales', qualify: true, options: [
    'Anglesey', 'Blaenau Gwent', 'Bridgend', 'Caerphilly', 'Cardiff',
    'Carmarthenshire', 'Ceredigion', 'Conwy', 'Denbighshire', 'Flintshire',
    'Gwynedd', 'Merthyr Tydfil', 'Monmouthshire', 'Neath Port Talbot', 'Newport',
    'Pembrokeshire', 'Powys', 'Rhondda Cynon Taf', 'Swansea', 'Torfaen',
    'Vale of Glamorgan', 'Wrexham',
  ] },
  { group: 'Northern Ireland', qualify: true, options: [
    'County Antrim', 'County Armagh', 'County Down', 'County Fermanagh',
    'County Londonderry', 'County Tyrone',
  ] },
];

const IRELAND = [
  { group: 'Leinster', options: [
    'County Carlow', 'County Dublin', 'County Kildare', 'County Kilkenny',
    'County Laois', 'County Longford', 'County Louth', 'County Meath',
    'County Offaly', 'County Westmeath', 'County Wexford', 'County Wicklow',
  ] },
  { group: 'Munster', options: [
    'County Clare', 'County Cork', 'County Kerry', 'County Limerick',
    'County Tipperary', 'County Waterford',
  ] },
  { group: 'Connacht', options: [
    'County Galway', 'County Leitrim', 'County Mayo', 'County Roscommon', 'County Sligo',
  ] },
  { group: 'Ulster (Republic)', options: ['County Cavan', 'County Donegal', 'County Monaghan'] },
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'District of Columbia', 'Florida', 'Georgia',
  'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina',
  'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
  'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];

const SPAIN = [
  { group: 'Golf coasts', heading: true, options: [
    'Costa del Sol', 'Costa Blanca', 'Costa Brava', 'Costa de la Luz', 'Costa Cálida',
  ] },
  { group: 'Autonomous communities', heading: true, options: [
    'Andalusia', 'Aragon', 'Asturias', 'Balearic Islands', 'Basque Country',
    'Canary Islands', 'Cantabria', 'Castile and León', 'Castilla-La Mancha',
    'Catalonia', 'Extremadura', 'Galicia', 'La Rioja', 'Madrid', 'Murcia',
    'Navarre', 'Valencian Community',
  ] },
];

const JAPAN = [
  { group: 'Hokkaido', options: ['Hokkaido'] },
  { group: 'Tohoku', options: ['Aomori', 'Iwate', 'Miyagi', 'Akita', 'Yamagata', 'Fukushima'] },
  { group: 'Kanto', options: ['Ibaraki', 'Tochigi', 'Gunma', 'Saitama', 'Chiba', 'Tokyo', 'Kanagawa'] },
  { group: 'Chubu', options: [
    'Niigata', 'Toyama', 'Ishikawa', 'Fukui', 'Yamanashi', 'Nagano', 'Gifu', 'Shizuoka', 'Aichi',
  ] },
  { group: 'Kansai', options: ['Mie', 'Shiga', 'Kyoto', 'Osaka', 'Hyogo', 'Nara', 'Wakayama'] },
  { group: 'Chugoku', options: ['Tottori', 'Shimane', 'Okayama', 'Hiroshima', 'Yamaguchi'] },
  { group: 'Shikoku', options: ['Tokushima', 'Kagawa', 'Ehime', 'Kochi'] },
  { group: 'Kyushu & Okinawa', options: [
    'Fukuoka', 'Saga', 'Nagasaki', 'Kumamoto', 'Oita', 'Miyazaki', 'Kagoshima', 'Okinawa',
  ] },
];

export const COUNTRIES = [
  { label: 'United Kingdom', value: 'United Kingdom', regionLabel: 'County', regions: UK },
  { label: 'Ireland',        value: 'Ireland',        regionLabel: 'County', regions: IRELAND },
  { label: 'United States',  value: 'United States',  regionLabel: 'State',  regions: US_STATES },
  { label: 'Australia',      value: 'Australia',      regionLabel: 'State',  regions: [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory',
    'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia',
  ] },
  { label: 'Spain',          value: 'Spain',          regionLabel: 'Region', regions: SPAIN },
  { label: 'Portugal',       value: 'Portugal',       regionLabel: 'Region', regions: [
    'Algarve', 'Alentejo', 'Lisbon area', 'Silver Coast', 'Porto and the North',
    'Madeira', 'Azores',
  ] },
  { label: 'France',         value: 'France',         regionLabel: 'Region', regions: [
    'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany',
    'Centre-Val de Loire', 'Corsica', 'Grand Est', 'Hauts-de-France',
    'Île-de-France', 'Normandy', 'Nouvelle-Aquitaine', 'Occitanie',
    'Pays de la Loire', "Provence-Alpes-Côte d'Azur",
  ] },
  { label: 'Germany',        value: 'Germany',        regionLabel: 'State',  regions: [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
    'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia',
    'Rhineland-Palatinate', 'Saarland', 'Saxony', 'Saxony-Anhalt',
    'Schleswig-Holstein', 'Thuringia',
  ] },
  { label: 'Sweden',         value: 'Sweden',         regionLabel: 'County', regions: [
    'Blekinge', 'Dalarna', 'Gotland', 'Gävleborg', 'Halland', 'Jämtland',
    'Jönköping', 'Kalmar', 'Kronoberg', 'Norrbotten', 'Skåne', 'Stockholm',
    'Södermanland', 'Uppsala', 'Värmland', 'Västerbotten', 'Västernorrland',
    'Västmanland', 'Västra Götaland', 'Örebro', 'Östergötland',
  ] },
  { label: 'Denmark',        value: 'Denmark',        regionLabel: 'Region', regions: [
    'Capital Region of Denmark', 'Central Denmark', 'North Denmark', 'Zealand',
    'Southern Denmark',
  ] },
  { label: 'Netherlands',    value: 'Netherlands',    regionLabel: 'Province', regions: [
    'Drenthe', 'Flevoland', 'Friesland', 'Gelderland', 'Groningen', 'Limburg',
    'North Brabant', 'North Holland', 'Overijssel', 'South Holland', 'Utrecht', 'Zeeland',
  ] },
  { label: 'South Africa',   value: 'South Africa',   regionLabel: 'Province', regions: [
    'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal', 'Limpopo',
    'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
  ] },
  { label: 'New Zealand',    value: 'New Zealand',    regionLabel: 'Region', regions: [
    'Northland', 'Auckland', 'Waikato', 'Bay of Plenty', 'Gisborne', "Hawke's Bay",
    'Taranaki', 'Manawatū-Whanganui', 'Wellington', 'Tasman', 'Nelson',
    'Marlborough', 'West Coast', 'Canterbury', 'Otago', 'Southland',
  ] },
  { label: 'Canada',         value: 'Canada',         regionLabel: 'Province', regions: [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
    'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 'Nunavut',
    'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon',
  ] },
  { label: 'Japan',          value: 'Japan',          regionLabel: 'Prefecture', regions: JAPAN },
];

/**
 * The picker's options for one country, as groups the select renders
 * directly: [{ group: string|null, options: [{ value, label }] }]. A flat
 * country comes back as one group with no heading.
 */
export function regionOptions(country) {
  const entry = COUNTRIES.find(c => c.value === country);
  if (!entry || entry.regions.length === 0) return [];
  if (typeof entry.regions[0] === 'string') {
    return [{ group: null, options: entry.regions.map(r => ({ value: r, label: r })) }];
  }
  return entry.regions.map(({ group, options, qualify, heading }) => {
    const opts = options.map(o => ({ value: qualify ? `${o}, ${group}` : o, label: o }));
    if (!heading && !(options.length === 1 && options[0] === group)) {
      opts.unshift({ value: group, label: `All of ${group}` });
    }
    return { group, options: opts };
  });
}

/** Is `region` one of this country's options? (An old batch's coarse region,
 *  "East Coast", is not — a copied job starts from the whole country.) */
export function isKnownRegion(country, region) {
  return regionOptions(country).some(g => g.options.some(o => o.value === region));
}
